import { Component, Inject, OnInit, Optional, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClientAdminService } from '../client/client-admin.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-workflow-edit-modal',
    templateUrl: './workflow-edit-modal.component.html',
    styleUrls: ['./workflow-edit-modal.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowEditModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  title = 'Edit ';
  minimise = false;
  disableAll = false;
  saving = false;

  name = '';
  description = '';
  isActive = '';
  isSelected = '';
  versionNumber = '';

  versionNumList: any[] = [];
  compList: any[] = [];
  node: any = {};
  private nodeCopy: any = {};
  private compListCopy: any[] = [];
  dirty: Record<string, boolean> = {};

  selectedActiveOptions = [{ name: 'Yes', value: '1' }, { name: 'No', value: '0' }];

  constructor(
    @Optional() public dialogRef: MatDialogRef<WorkflowEditModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private clientAdminService: ClientAdminService,
    private snackbar: GtwSnackbarService,
  ) {}

  ngOnInit(): void {
    this.node = this.data?.node || {};
    this.compList = this.data?.compList || [];
    this.versionNumList = this.data?.versionNumList || [];

    this.name = this.node.name || '';
    this.description = this.node.description || '';
    this.isActive = this.node.is_active || '';
    this.isSelected = String(this.node.is_selected || '');
    this.versionNumber = this.node.version_number || '';

    this.nodeCopy = JSON.parse(JSON.stringify(this.node));
    this.compListCopy = JSON.parse(JSON.stringify(this.compList));

    const level = parseInt(this.node.nav_level, 10);
    this.title += level === 1 ? 'Category' : 'Step';
  }

  isNameMissing(): boolean {
    return this.dirty['name'] && !this.name?.trim();
  }

  changeVersion(ver: any): void {
    this.disableAll = true;
    this.name = ver.name;
    this.description = ver.description;
    this.isActive = ver.is_Active;
    this.versionNumber = ver.version_number;
  }

  reset(): void {
    this.disableAll = false;
    this.name = this.nodeCopy.name;
    this.description = this.nodeCopy.description;
    this.isActive = this.nodeCopy.is_active;
    this.isSelected = String(this.nodeCopy.is_selected);
    this.versionNumber = this.nodeCopy.version_number;
  }

  save(): void {
    this.dirty['name'] = true;
    if (this.isNameMissing()) return;

    let updInsType = 'I';
    const cp = this.compListCopy[0] || {};
    if (this.name === this.nodeCopy.name && this.description === this.nodeCopy.description &&
        this.versionNumber === this.nodeCopy.version_number &&
        this.isSelected === String(this.nodeCopy.is_selected) &&
        this.isActive === this.nodeCopy.is_active) {
      this.snackbar.openSnackBar('No changes to update.', 'gtw-snackbar--yellow');
      return;
    }
    if (this.disableAll || this.isActive !== this.nodeCopy.is_active) {
      updInsType = 'U';
    }

    this.saving = true;
    const nodeArr = [this.node];

    const params: Record<string, any> = {
      name: this.name,
      description: this.description,
      isActive: this.isActive,
      isSelected: this.isSelected,
      versionNumber: this.versionNumber,
      templatePath: cp.templatePath || '',
      controllerPath: cp.controllerPath || '',
      selectedModalSize: cp.selectedModalSize || '',
      parentCssClass: cp.parentCssClass || '',
      selectedActionType: cp.selectedActionType || '',
      updInsType,
      node: JSON.stringify(nodeArr),
      activityKey: '',
      screenKey: '',
      filter_json: '',
    };

    this.clientAdminService.editWorkflow(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (resp: any) => {
        this.saving = false;
        const data = resp?.data ?? resp;
        if (data?.result?.value > 0) {
          this.snackbar.openSnackBar(data.result.message || 'Updated successfully.', 'gtw-snackbar--green');
          this.dialogRef.close({ saved: true });
        } else {
          this.snackbar.openSnackBar(data?.result?.message || 'Error updating.', 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.saving = false;
        this.snackbar.openSnackBar('Error updating workflow.', 'gtw-snackbar--red');
      },
    });
  }

  close(): void {
    this.reset();
    this.dialogRef.close();
  }
}
